import React, { useEffect, useState } from 'react';
import Utilities from '@util/Utilities';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import Spinner from '@app/component/static/Spinner';
import Tag from '@app/component/tag/Tag';
import { Tooltip } from 'react-tooltip';
import Competitor from '@app/model/company_hotel/Competitor';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import _isEmpty from 'lodash/isEmpty';
import MarketStateDailySnapshotState from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import { useLatestProcessRequestExecutionStateMonitor } from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';
import Process from '@app/model/process-request/Process';
import Refunding from '@app/model/market_state/Refunding';
import Meal from '@app/model/market_state/Meal';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { PromotionTooltipCard, PromotionTooltip }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';

import { buildPromotionTooltipPayload } from '../../common/tooltip/PromotionIndicator.utilities';

export type CompetitorSnapshotsComparison = {
	current: MarketStateStatDateCompetitorSnapshot;
	previous: MarketStateStatDateCompetitorSnapshot | undefined;
}[];

export const DisplayMarketStateSnapshotTableHotelPriceFragment: React.FunctionComponent<{
	marketStateDailySnapshotState: MarketStateDailySnapshotState;
	competitor?: Competitor;
	roomMappingType?: HotelRoomMappingType;
}> = ({ marketStateDailySnapshotState, competitor, roomMappingType }) => {
	const tools = useMarketStateTools();
	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const { companyHotel } = useSelectedCompanyHotel();
	const { filter } = useMarketStateFilterInputTools();
	const [competitorSnapshots, setCompetitorSnapshots] = useState<CompetitorSnapshotsComparison>([]);
	const showUpdateSuccess =
		marketStateDailySnapshotState.calendarDay.isCurrentMonth &&
		!marketStateDailySnapshotState.calendarDay.isPast &&
		isMarketStateTableProcess(monitor.latestCompletedExecutionState);
	const isUpdating =
		marketStateDailySnapshotState.calendarDay.isCurrentMonth &&
		!marketStateDailySnapshotState.calendarDay.isPast &&
		monitor.isInteractionLocked &&
		isMarketStateTableProcess(monitor.latestActiveExecutionState);

	useEffect(() => {
		if (
			!marketStateDailySnapshotState.needsRefreshResults() &&
			!!marketStateDailySnapshotState.marketStateDailySnapshot
		) {
			marketStateDailySnapshotState.marketStateDailySnapshot
				.getCompetitorSnapshotsComparison(tools.findPreviousSnapshot)
				.then(snapshots => {
					setCompetitorSnapshots(snapshots);
				})
				.catch(() => undefined);
		}
	}, [marketStateDailySnapshotState.marketStateDailySnapshot, tools.findPreviousSnapshot]);

	if (marketStateDailySnapshotState.needsRefreshResults() || _isEmpty(competitorSnapshots))
		return <></>;

	return (
		<DisplayMarketStateSnapshotTableHotelPricePureFragment
			marketStateDailySnapshotState={marketStateDailySnapshotState}
			competitor={competitor}
			roomMappingType={roomMappingType}
			competitorSnapshots={competitorSnapshots}
			companyHotel={companyHotel}
			isAnyProvider={filter.provider === undefined}
			showUpdateSuccess={showUpdateSuccess}
			isUpdating={isUpdating}
		/>
	);
};

export const DisplayMarketStateSnapshotTableHotelPricePureFragment = React.memo(
	function DisplayMarketStateSnapshotTableHotelPricePureFragment({
		marketStateDailySnapshotState,
		competitor,
		roomMappingType,
		competitorSnapshots,
		companyHotel,
		isAnyProvider,
		showUpdateSuccess,
		isUpdating,
	}: {
		marketStateDailySnapshotState: MarketStateDailySnapshotState;
		competitor?: Competitor;
		roomMappingType?: HotelRoomMappingType;
		competitorSnapshots: CompetitorSnapshotsComparison;
		companyHotel: CompanyHotel;
		isAnyProvider: boolean;
		showUpdateSuccess: boolean;
		isUpdating: boolean;
	}) {
		if (marketStateDailySnapshotState.needsRefreshResults() || _isEmpty(competitorSnapshots))
			return <></>;

		const current = competitor
			? competitorSnapshots.find(snap => snap.current.competitorId === competitor.id)?.current
			: competitorSnapshots.find(snap => snap.current.isMainHotel)?.current;

		if (!current) return <></>;

		const previous = competitor
			? competitorSnapshots.find(snap => snap.previous?.competitorId === competitor.id)?.previous
			: competitorSnapshots.find(snap => snap.previous?.isMainHotel)?.previous;

		return (
			<>
				<div className="grid grid-cols-[minmax(0,1fr)_14px_8px_auto] items-center justify-items-start gap-x-0.5">
					{current.isLos() && (
						<DisplayPriceWithParity
							current={current}
							previous={previous}
							roomMappingType={roomMappingType}
							companyHotel={companyHotel}
							isAnyProvider={isAnyProvider}
							isLos
						/>
					)}
					{current.isSoldOut() && !current.isLos() && (
						<Tag state="critical" title="Sold Out">
							S/O
						</Tag>
					)}
					{current.isNotAvailable() && (
						<Tag state="none" title="Not Available">
							N/A
						</Tag>
					)}
					{current.isAvailable() && (
						<>
							<DisplayPriceWithParity
								current={current}
								previous={previous}
								roomMappingType={roomMappingType}
								companyHotel={companyHotel}
								isAnyProvider={isAnyProvider}
							/>
							<DisplayRatesAndMealIndicator current={current} roomMappingType={roomMappingType} />
						</>
					)}
					<div className="col-start-4 row-start-1 flex">
						{!marketStateDailySnapshotState.calendarDay.isPast && (
							<div
								className={Utilities.classNames(
									'text-green-300 opacity-0 transition-opacity ease-in-out delay-150 duration-500',
									showUpdateSuccess ? ' block opacity-100' : 'hidden'
								)}
							>
								<CheckCircleIcon className="h-4 w-4" />
							</div>
						)}
						{isUpdating && (
							<div>
								<Spinner width={3} height={3} />
							</div>
						)}
					</div>
				</div>
			</>
		);
	}
);

const DisplayRatesAndMealIndicator: React.FunctionComponent<{
	current: MarketStateStatDateCompetitorSnapshot;
	roomMappingType?: HotelRoomMappingType;
}> = ({ current, roomMappingType }) => {
	const lowestItemForRoom = roomMappingType
		? current.getLowestItemForRoomMappingType(roomMappingType)
		: current.getLowestItemForRoom();
	if (!lowestItemForRoom?.lowestItem?.item) return null;
	const meal = lowestItemForRoom.lowestItem.item.meal;
	const mealIndicator = meal != null ? getMealIndicator(meal) : undefined;
	const isFullyRefundable = lowestItemForRoom.lowestItem.item.refunding === Refunding.FULL;
	return (
		<span className="col-start-3 row-start-1 flex h-5 w-2 flex-col items-center justify-center gap-y-0.5">
			<span data-rate-indicator="flex" role={isFullyRefundable ? 'img' : undefined}
				tabIndex={isFullyRefundable ? 0 : undefined} aria-label={isFullyRefundable ? 'Fully refundable' : undefined}
				aria-hidden={!isFullyRefundable || undefined}
				data-tooltip-id={isFullyRefundable ? 'rates_meal_tooltip' : undefined}
				data-tooltip-content={isFullyRefundable ? 'Fully refundable' : undefined}
				className={Utilities.classNames('inline-flex h-2 w-2 shrink-0 cursor-pointer rounded-full bg-slate-100 ring-1 ring-inset ring-slate-700/10', !isFullyRefundable && 'invisible')} />
			<span data-rate-indicator="meal" role={mealIndicator ? 'img' : undefined}
				tabIndex={mealIndicator ? 0 : undefined} aria-label={mealIndicator?.tooltip}
				aria-hidden={!mealIndicator || undefined}
				data-tooltip-id={mealIndicator ? 'rates_meal_tooltip' : undefined}
				data-tooltip-content={mealIndicator?.tooltip}
				className={Utilities.classNames('inline-flex h-2 w-2 shrink-0 cursor-pointer rounded-full ring-1 ring-inset', mealIndicator?.className ?? 'invisible')} />
		</span>
	);
};

const getMealIndicator = (meal: Meal): { tooltip: string; className: string } | undefined => {
	switch (meal) {
		case Meal.BREAKFAST:
			return { tooltip: 'Bed & Breakfast', className: 'bg-green-100 ring-green-700/10' };
		case Meal.HALF_BOARD:
			return { tooltip: 'Half Board', className: 'bg-amber-100 ring-amber-700/10' };
		case Meal.FULL_BOARD:
			return { tooltip: 'Full Board', className: 'bg-blue-100 ring-blue-700/10' };
		case Meal.ALL_INCLUSIVE:
			return { tooltip: 'All Inclusive', className: 'bg-purple-100 ring-purple-700/10' };
		case Meal.NONE:
		default:
			return undefined;
	}
};

const isMarketStateTableProcess = (
	processRequestExecutionState: { process: Process } | null | undefined
) => {
	return (
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE ||
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE_OTA ||
		processRequestExecutionState?.process === Process.PRICE_RECOMMENDATION
	);
};

const DisplayPriceWithParity: React.FunctionComponent<{
	current: MarketStateStatDateCompetitorSnapshot;
	previous: MarketStateStatDateCompetitorSnapshot | undefined;
	roomMappingType: HotelRoomMappingType | undefined;
	companyHotel: CompanyHotel;
	isAnyProvider: boolean;
	isLos?: boolean;
}> = ({ current, previous, roomMappingType, companyHotel, isAnyProvider, isLos }) => {
	const currentLowestItem = !roomMappingType
		? current.getLowestItemForRoom()
		: current.getLowestItemForRoomMappingType(roomMappingType);

	if (
		!currentLowestItem ||
		!currentLowestItem.lowestItem ||
		!currentLowestItem.lowestItem.item ||
		!currentLowestItem.lowestItem.item.price
	)
		return (
			<Tag state="none" title="Not Available">
				N/A
			</Tag>
		);

	let difference = 0;

	if (previous !== undefined && current.isAvailable() && previous.isAvailable()) {
		const previousPrice = previous.getLowestItemForRoom().lowestItem.item.price.amount;
		difference = previousPrice - currentLowestItem.lowestItem.item.price.amount;
	}

	const roomWithMapping = currentLowestItem.findRoom(companyHotel);

	// The registry key must be unique per anchor, not just per item: in CLASSIC view the
	// aggregate price cell (roomMappingType undefined) and an expanded per-room-type detail
	// cell can resolve to the SAME item id, and a shared key would let one cell's unmount
	// cleanup delete the entry the other still needs.
	const tooltipKey = `${currentLowestItem.lowestItem.item.id}:${roomMappingType ?? 'ALL'}:${
		isLos ? 'LOS' : 'STD'
	}`;

	const displayParity = difference != 0 && !isAnyProvider;

	const hasDifferentRoom =
		!!previous && current.getLowestItemForRoom().roomId !== previous.getLowestItemForRoom().roomId;

	const previousRoomName = previous?.getLowestItemForRoom().findRoomName(companyHotel);

	const displayArrow = (difference: number) => {
		return difference < 0 ? (
			<ArrowUpIcon className="w-3.5 h-3.5 text-green-500" />
		) : (
			<ArrowDownIcon className="w-3.5 h-3.5 text-red-500" />
		);
	};

	return (
		<>
			{!isLos && <span className="min-w-0 cursor-help tabular-nums" tabIndex={0}
				data-tooltip-id="lowest-item-room-tooltip" data-lowest-item-room-tooltip-key={tooltipKey}>{Utilities.formatPrice(currentLowestItem.lowestItem.item.price)}</span>}
			{isLos && (
				<div className="col-span-2 flex items-center justify-start gap-x-1">
					<span className="cursor-help tabular-nums text-gray-400 italic" tabIndex={0}
						data-tooltip-id="lowest-item-room-tooltip" data-lowest-item-room-tooltip-key={tooltipKey}>
						{Utilities.formatPrice(currentLowestItem.lowestItem.item.price)}
					</span>
					<span className="inline-flex items-center rounded-md bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/10">
						L{currentLowestItem.lowestItem.item.leastPeriodOfStay}
					</span>
				</div>
			)}
			<span
				className="inline-flex h-3.5 w-3.5 shrink-0 cursor-pointer items-center justify-center"
				data-tooltip-id="lowest-item-room-tooltip"
				data-lowest-item-room-tooltip-key={tooltipKey}
			>
				{displayParity && displayArrow(difference)}
			</span>
			<RegisterLowestItemRoomTooltipContent tooltipKey={tooltipKey}>
				<><dl data-rate-room-details className="divide-y divide-gray-200 border-t border-b border-gray-200">
					<div className="py-1.5 px-3 text-xs">
						<p className="font-medium text-gray-800 mb-1">Current Room Name</p>
						<p className="font-normal text-gray-500">
							<span>{roomWithMapping?.roomName}</span>
							<span className="ml-1 text-gray-400 font-light italic">
								({companyHotel.findRoomMappingTypeName(roomWithMapping?.roomMappingType)})
							</span>
						</p>
					</div>
					{displayParity && (
						<>
							<div className="py-1.5 px-3 text-xs">
								<p className="font-medium text-gray-800 mb-1">
									Compared with {companyHotel.settings.calendar.numberOfDaysForPriceParity} days ago
								</p>
								<div className="flex items-center gap-x-1">
									{displayArrow(difference)}
									<span className="font-medium text-gray-500">
										{Utilities.formatAmount(
											Math.abs(difference),
											previous!.getLowestItemForRoom().lowestItem.item.price.currency
										)}
									</span>
									{hasDifferentRoom && <span className="text-gray-400 font-light italic">(*)</span>}
								</div>
							</div>
							{hasDifferentRoom && (
								<div className="py-1.5 px-3 text-xs">
									<p className="font-medium text-gray-800 mb-1">
										<span className="text-gray-400 font-light italic mr-1">(*)</span>Room name
										change
									</p>
									<p className="text-gray-500 space-x-1">
										<span>Today:</span>
										<span>{Utilities.formatPrice(currentLowestItem.lowestItem.item.price)}</span>
										<span>-</span>
										<span>{roomWithMapping?.roomName}</span>
									</p>
									<p className="text-gray-500 space-x-1">
										<span>
											{Utilities.formatDateString(
												previous!.getLowestItemForRoom().lowestItem.item.creationDate,
												'DD/MM'
											)}
											:
										</span>
										<span>
											{Utilities.formatPrice(
												previous!.getLowestItemForRoom().lowestItem.item.price
											)}
										</span>
										<span>-</span>
										<span>{previousRoomName}</span>
									</p>
								</div>
							)}
						</>
					)}
				</dl>
				<div className="px-3 py-2">
					<PromotionTooltipCard payload={buildPromotionTooltipPayload(
						currentLowestItem.lowestItem.item.promotions,
						currentLowestItem.lowestItem.item.preferredTier,
						amount => Utilities.formatAmount(amount, currentLowestItem.lowestItem.item.price.currency)
					)} />
				</div>
				</>
			</RegisterLowestItemRoomTooltipContent>
		</>
	);
};

const lowestItemRoomTooltipContents = new Map<string, React.ReactNode>();

const RegisterLowestItemRoomTooltipContent: React.FunctionComponent<
	React.PropsWithChildren<{ tooltipKey: string }>
> = ({ tooltipKey, children }) => {
	useEffect(() => {
		lowestItemRoomTooltipContents.set(tooltipKey, children);
		return () => {
			if (lowestItemRoomTooltipContents.get(tooltipKey) === children) {
				lowestItemRoomTooltipContents.delete(tooltipKey);
			}
		};
	});

	return null;
};

export const MarketStateSnapshotTableSharedTooltips: React.FunctionComponent = () => {
	return (
		<>
			<Tooltip id="rates_meal_tooltip" className="z-50" />
			<Tooltip id="rates_flex_tooltip" className="z-50" />
			<Tooltip id="rates_flex_legend_tooltip" className="z-50 max-w-xs" />
			<PromotionTooltip id="rates_promotion_tooltip" />
			<Tooltip
				id="lowest-item-room-tooltip"
				place="top"
				noArrow
				className="!bg-white shadow ring-1 !ring-black !ring-opacity-5 !rounded-md !text-left !p-0 !opacity-100 !z-50"
				render={({ activeAnchor }) => {
					const tooltipKey = activeAnchor?.getAttribute('data-lowest-item-room-tooltip-key');
					if (!tooltipKey) return null;
					return lowestItemRoomTooltipContents.get(tooltipKey) ?? null;
				}}
			/>
		</>
	);
};
